import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { FiHeart, FiMapPin } from "react-icons/fi";
import "react-datepicker/dist/react-datepicker.css"; 
import axios from "axios";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import NavigateHome from "../../component/utils/NavigateHome";
import PageCount from "../../component/utils/PageCount";

function BasicInfo() {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    profile_for: "",
    gender: "",
    dob: null,
    current_location: "",
    height: "",
    marital_status: "",
  });

const navigate = useNavigate();

  const [locationOptions, setLocationOptions] = useState([]);
  const [loadingLocation, setLoadingLocation] = useState(false);

  const [errors, setErrors] = useState({});
  const handlePhoneChange = (e) => {
  let value = e.target.value;

  // Allow only digits
  value = value.replace(/\D/g, "");

  // Update form
  handleChange({
    target: { name: "phone", value }
  });

  // Validation
  if (value.length !== 10) {
    setErrors((prev) => ({ ...prev, phone: "Phone number must be 10 digits" }));
  } else {
    setErrors((prev) => ({ ...prev, phone: "" }));
  }
};


  // Generate height options (4ft 0in to 7ft 0in)
const heightOptions = [];

for (let ft = 4; ft <= 7; ft++) {
  for (let inch = 0; inch < 12; inch++) {
    // Convert to cm
    const cm = Math.round((ft * 30.48) + (inch * 2.54));

    heightOptions.push({
      value: `${ft}ft ${inch}"`,   // Display value
      label: `${ft}ft ${inch}" (${cm} cm)`,  // Better UI
      feet: ft,
      inch: inch,
      cm: cm
    });
  }
}

useEffect(() => {
  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(`${baseurl}getUserProfile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Profile Response:", response.data);

      const data = response.data.profile;

      if (data) {
        // Convert numeric height (e.g., 5.42) to feet/inch
        let heightFeet = null;
        let heightInch = null;

        if (data.height) {
          const totalFeet = parseFloat(data.height);
          heightFeet = Math.floor(totalFeet);
          heightInch = Math.round((totalFeet - heightFeet) * 12);
        }

        setFormData({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          email: data.email || "",
          phone: data.phone || "",
          profile_for: data.profile_for || "",
          gender: data.gender || "",
          dob: data.date_of_birth ? new Date(data.date_of_birth) : null,
          current_location: data.location || "",
          height: data.height || "",       // numeric value for backend
          height_feet: heightFeet,         // for UI
          height_inch: heightInch,         // for UI
          marital_status: data.marital_status || "",
        });
      }
    } catch (error) {
      console.log("Profile Fetch Error:", error);
    }
  };

  fetchProfile();
}, []);


  // React-Select Styles (Optional for better UI)
  const customSelectStyles = {
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  // Dropdown Options
  const profileForOptions = [
    { value: "self", label: "Myself" },
    { value: "brother", label: "Brother" },
    { value: "sister", label: "Sister" },
    { value: "son", label: "Son" },
    { value: "daughter", label: "Daughter" },
    { value: "relative", label: "Relative" },
    { value: "friend", label: "Friend" },
  ];

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];

  const maritalOptions = [
    { value: "single", label: "Single" },
    { value: "divorced", label: "Divorced" },
    { value: "widowed", label: "Widowed" },
    { value: "separated", label: "Separated" },
  ];

  // Handle React Select change
  const handleSelectChange = (field, option) => {
    setFormData({ ...formData, [field]: option ? option.value : "" });
  };

  // Input fields change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /**
   * -------------------------------
   *  LOCATION AUTOCOMPLETE (REACT-SELECT)
   * -------------------------------
   */

  // Debounce function to limit API calls
  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const fetchLocation = async (query) => {
    if (!query) return;

    setLoadingLocation(true);

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${query}&addressdetails=1`,
        {
          headers: { "User-Agent": "soulsathi-app" },
        }
      );

      const data = await res.json();

      const locList = data.slice(0, 10).map((item) => ({
        label: item.display_name,
        value: item.display_name,
        city: item.address.city || item.address.town || item.address.village,
        state: item.address.state,
        country: item.address.country,
        lat: item.lat,
        lon: item.lon,
      }));

      setLocationOptions(locList);
    } catch (error) {
      console.log("Location API Error:", error);
    }

    setLoadingLocation(false);
  };

  // Debounced API function
  const debouncedFetchLocation = useCallback(debounce(fetchLocation, 400), []);

const handleSubmit = async (e) => {
  e.preventDefault();

  const token = localStorage.getItem("token");

  if (!token) {
    Swal.fire({
      toast: true,
      icon: "error",
      title: "No token found. Please login again.",
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: { popup: "swal2-toast-custom" },   // ⭐ CUSTOM CLASS
    });
    return;
  }

  const payload = { ...formData };
  delete payload.email;
  delete payload.height_cm;
  delete payload.height_feet;
  delete payload.height_inch;

  if (payload.height_feet != null && payload.height_inch != null) {
    payload.height = +(payload.height_feet + payload.height_inch / 12).toFixed(2);
  }

  if (!payload.height || isNaN(payload.height)) {
    Swal.fire({
      toast: true,
      icon: "error",
      title: "Please select a valid height",
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: { popup: "swal2-toast-custom" },   // ⭐ CUSTOM CLASS
    });
    return;
  }

  if (payload.dob instanceof Date) {
    const dob = payload.dob;
    const yyyy = dob.getFullYear();
    const mm = String(dob.getMonth() + 1).padStart(2, "0");
    const dd = String(dob.getDate()).padStart(2, "0");
    payload.date_of_birth = `${yyyy}-${mm}-${dd}`;
    delete payload.dob;
  }

  try {
    const res = await axios.post(`${baseurl}updateUserBasicInfo`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    Swal.fire({
      toast: true,
      icon: "success",
      title: "Profile updated successfully!",
      position: "top-end",
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      customClass: { popup: "swal2-toast-custom" },   // ⭐ CUSTOM CLASS
    });

    setTimeout(() => {
      navigate("/profile-info");
    }, 800);

  } catch (error) {
    Swal.fire({
      toast: true,
      icon: "error",
      title: "Failed to update profile!",
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: { popup: "swal2-toast-custom" },   // ⭐ CUSTOM CLASS
    });
  }
};



  return (
    <section className="basicinfo-section">
      <PageCount pageName = {"Basic User Info"}/>
      <NavigateHome/>
      <div className="basicinfo-container">
        <div className="basicinfo-header text-center mb-4">
          <FiHeart className="header-icon" />
          <h2>Basic Information</h2>
          <p>
            Let’s begin your journey with <b>Soulsathi.com</b> ❤️
          </p>
        </div>

        <form className="basicinfo-form" onSubmit={handleSubmit}>
          <div className="row g-2">
            {/* First Name */}
            <div className="col-md-6">
              <label>First Name</label>
              <input
                type="text"
                name="first_name"
                placeholder="Enter your first name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>

            {/* Last Name */}
            <div className="col-md-6">
              <label>Last Name</label>
              <input
                type="text"
                name="last_name"
                placeholder="Enter your last name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>

            {/* Email */}
            <div className="col-md-4">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                 
                readOnly   
              />
            </div>

             {/* Phone */}
          <div className="col-md-4">
  <label>Phone Number</label>
  <input
    type="tel"
    name="phone"
    placeholder="Enter your phone number"
    value={formData.phone}
    onChange={handlePhoneChange}
    maxLength="10"
    required
  />
  {errors.phone && <small className="text-danger">{errors.phone}</small>}
</div>




            {/* Profile For */}
            <div className="col-md-4">
              <label>Who is this profile for?</label>
              <Select
  options={profileForOptions} classNamePrefix="talaash-select"
  value={profileForOptions.find(opt => opt.value === formData.profile_for) || null}
  onChange={(opt) => handleSelectChange("profile_for", opt)}
/>
            </div>


            

            {/* Gender */}
            <div className="col-3">
              <label>Gender</label>
             <Select
  options={genderOptions} classNamePrefix="talaash-select"
  value={genderOptions.find(opt => opt.value === formData.gender) || null}
  onChange={(opt) => handleSelectChange("gender", opt)}
/>
            </div>

            {/* DOB */}
            <div className="col-3">
  <label>Date of Birth</label>
  <DatePicker
    selected={formData.dob}
    onChange={(date) => setFormData({ ...formData, dob: date })}
    placeholderText="Choose your dob"
    dateFormat="dd MMM yyyy"
    className="datepicker-input"
    showMonthDropdown
    showYearDropdown
    dropdownMode="select"
    maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 18))} // Max date = today minus 18 years
    required
  />
</div>



            {/* Height */}
           <div className="col-md-3">
  <label>Height</label>
<Select
  options={heightOptions}
  placeholder="Select Height"
  classNamePrefix="talaash-select"
  styles={customSelectStyles}
  value={
    formData.height_feet != null
      ? heightOptions.find(
          (opt) =>
            opt.feet === formData.height_feet &&
            opt.inch === formData.height_inch
        )
      : null
  }
  onChange={(opt) => {
    const feet = opt.feet;
    const inches = opt.inch;
    const numericHeight = +(feet + inches / 12).toFixed(2); // e.g., 5.42

    setFormData({
      ...formData,
      height: numericHeight, // Postgres numeric
      height_cm: opt.cm,     // optional
      height_feet: feet,
      height_inch: inches,
    });
  }}
/>
</div>
            {/* Marital Status */}
            <div className="col-md-3">
              <label>Marital Status</label>
              <Select
  options={maritalOptions} classNamePrefix="talaash-select"
  value={maritalOptions.find(opt => opt.value === formData.marital_status) || null}
  onChange={(opt) => handleSelectChange("marital_status", opt)}
/>
            </div>


            {/* Current Location React Select Autocomplete */}
  <div className="col-12">
  <label>Current Location</label>

  <Select
    options={locationOptions}
    placeholder="Search location"
    isLoading={loadingLocation}
    value={
      formData.current_location
        ? { label: formData.current_location, value: formData.current_location }
        : null
    }
    onInputChange={(inputValue) => {
      if (inputValue.length >= 2) {
        debouncedFetchLocation(inputValue);
      }
    }}
    onChange={(selected) => {
      setFormData({
        ...formData,
        current_location: selected?.label || "",
      });
    }}
    classNamePrefix="talaash-select"
    styles={customSelectStyles}
    components={{
      DropdownIndicator: () => (
        <FiMapPin size={18} style={{ marginRight: "8px", color: "#555" }} />
      ),
      IndicatorSeparator: () => null,
    }}
  />
</div>

            
          </div>

          <div className="text-center mt-4">
            <button type="submit" className="btn-primary">
              Continue →
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default BasicInfo;
